import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { ExpenseTypeService } from '../../services/expense-type.service';
import * as ExpenseTypeActions from './expense-type.actions';

@Injectable()
export class ExpenseTypesEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _expenseTypeService = inject(ExpenseTypeService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all expensetypes',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All ExpenseTypes
    getAllExpenseTypes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseTypeActions.loadExpenseTypes),
            switchMap(() => {
                return this._expenseTypeService.getAll().pipe(
                    map((res) => {
                        return ExpenseTypeActions.loadExpenseTypesSuccess({
                            expenseTypes: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseTypeActions.loadExpenseTypesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One ExpenseType
    getExpenseType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseTypeActions.loadExpenseType),
            switchMap((action) => {
                return this._expenseTypeService.getOne(action.id).pipe(
                    map((res) => {
                        return ExpenseTypeActions.loadExpenseTypeSuccess({
                            expenseType: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseTypeActions.loadExpenseTypeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create ExpenseType
    createExpenseType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseTypeActions.createExpenseType),
            switchMap((action) => {
                return this._expenseTypeService.create(action.data).pipe(
                    map((res) => {
                        return ExpenseTypeActions.createExpenseTypeSuccess({
                            expenseType: res.data,
                            success: 'Expense Type created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseTypeActions.createExpenseTypeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update ExpenseType
    updateExpenseType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseTypeActions.updateExpenseType),
            switchMap((action) => {
                return this._expenseTypeService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return ExpenseTypeActions.updateExpenseTypeSuccess({
                                expenseType: res.data,
                                success: 'Expense Type updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                ExpenseTypeActions.updateExpenseTypeFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete ExpenseType
    deleteExpenseType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseTypeActions.deleteExpenseType),
            switchMap((action) => {
                return this._expenseTypeService.delete(action.id).pipe(
                    map(() => {
                        return ExpenseTypeActions.deleteExpenseTypeSuccess({
                            id: action.id,
                            success: 'Expense Type deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseTypeActions.deleteExpenseTypeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onExpenseTypeActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ExpenseTypeActions.createExpenseTypeSuccess,
                    ExpenseTypeActions.updateExpenseTypeSuccess,
                    ExpenseTypeActions.deleteExpenseTypeSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onExpenseTypeActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ExpenseTypeActions.loadExpenseTypesFailure,
                    ExpenseTypeActions.loadExpenseTypeFailure,
                    ExpenseTypeActions.createExpenseTypeFailure,
                    ExpenseTypeActions.updateExpenseTypeFailure,
                    ExpenseTypeActions.deleteExpenseTypeFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
